import React, { useCallback, useEffect, useState } from 'react';
import { Send, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { apiClient } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const api = {
  list: () => apiClient.get('/hr/attendance/approvals').then((r) => r.data),
  submit: (p) => apiClient.post('/hr/attendance/approvals', p).then((r) => r.data),
  decide: (id, p) => apiClient.patch(`/hr/attendance/approvals/${id}`, p).then((r) => r.data),
  summary: (y, m) => apiClient.get(`/hr/attendance/summary?year=${y}&month=${m}`).then((r) => r.data),
};
const COUNTRIES = [['', 'All'], ['IN', 'India'], ['UK', 'UK'], ['MY', 'Malaysia']];

const AttendanceApprovalsPage = () => {
  const now = new Date();
  const [items, setItems] = useState([]);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [country, setCountry] = useState('');
  const [comment, setComment] = useState('');
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([api.list(), api.summary(year, month)]);
      setItems(a.data || []);
      setSummary(s.data || []);
    } finally { setLoading(false); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    try {
      await api.submit({ period_year: year, period_month: month, country: country || null, comment });
      toast({ title: 'Submitted for approval' }); setComment(''); load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const decide = async (id, decision) => {
    try { await api.decide(id, { decision }); toast({ title: decision }); load(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const totalLwp = summary.reduce((sum, s) => sum + (s.lwp_days || 0), 0);
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Attendance Approvals</span>
        <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Monthly sign-off</h1>
        <p className="mt-1 text-xs sm:text-sm text-[#0b1a2e]/60">Submit a whole month's attendance for approval before running payroll.</p>
      </div>
      <div className="bg-white border border-black/5 rounded-2xl p-5 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Year</label>
            <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} data-testid="app-year" className="w-full px-3 py-2 border rounded text-sm" /></div>
          <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Month</label>
            <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} data-testid="app-month" className="w-full px-3 py-2 border rounded text-sm bg-white">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' })}</option>)}
            </select></div>
          <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Country</label>
            <select value={country} onChange={(e) => setCountry(e.target.value)} className="w-full px-3 py-2 border rounded text-sm bg-white">
              {COUNTRIES.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
            </select></div>
          <div className="md:col-span-2"><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Comment (optional)</label>
            <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="e.g. All present except Rahul" className="w-full px-3 py-2 border rounded text-sm" /></div>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 bg-[#f7fbf8] p-3 rounded-lg text-xs">
          <div><div className="text-[10px] uppercase text-[#0b1a2e]/60">Employees</div><div className="text-lg font-bold text-[#0b1a2e]">{summary.length}</div></div>
          <div><div className="text-[10px] uppercase text-[#0b1a2e]/60">Total LWP</div><div className="text-lg font-bold text-red-600" data-testid="total-lwp">{totalLwp.toFixed(1)} d</div></div>
          <div><div className="text-[10px] uppercase text-[#0b1a2e]/60">Total present</div><div className="text-lg font-bold text-emerald-700">{summary.reduce((a, s) => a + (s.present || 0), 0)}</div></div>
          <div><div className="text-[10px] uppercase text-[#0b1a2e]/60">Total absent</div><div className="text-lg font-bold text-red-600">{summary.reduce((a, s) => a + (s.absent || 0), 0)}</div></div>
        </div>
        <button onClick={submit} data-testid="submit-approval" className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold">
          <Send className="w-3.5 h-3.5" /> Submit month for approval
        </button>
      </div>
      {loading ? <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#0f5c3d] inline" /></div> : (
        <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf8]"><tr>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Period</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Country</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Submitted by</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Comment</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Status</th>
              <th></th>
            </tr></thead>
            <tbody>{items.map((r) => (
              <tr key={r.id} className="border-t border-black/5" data-testid={`approval-${r.id}`}>
                <td className="p-2 font-mono text-xs">{r.period_year}-{String(r.period_month).padStart(2, '0')}</td>
                <td className="p-2 text-xs">{r.country || 'ALL'}</td>
                <td className="p-2 text-xs">{r.submitted_by}</td>
                <td className="p-2 text-xs text-[#0b1a2e]/70">{r.comment || '—'}</td>
                <td className="p-2"><span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${r.status === 'pending' ? 'bg-amber-100 text-amber-800' : r.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>{r.status}</span></td>
                <td className="p-2 text-right">
                  {r.status === 'pending' && (
                    <div className="inline-flex gap-1">
                      <button onClick={() => decide(r.id, 'approved')} data-testid={`approve-${r.id}`} className="px-2 py-1 rounded bg-emerald-600 text-white text-[10px] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Approve</button>
                      <button onClick={() => decide(r.id, 'rejected')} data-testid={`reject-${r.id}`} className="px-2 py-1 rounded bg-red-600 text-white text-[10px] inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>
          {items.length === 0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No approvals yet.</div>}
        </div>
      )}
    </div>
  );
};

export default AttendanceApprovalsPage;
